import { EmptyChartState } from "@/components/common/EmptyChartState";
import type { EndorsementTypeCount } from "@/types";
import { Divider, Box, Typography } from "@mui/material";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useAppTheme } from "@/hooks/useAppTheme";
import { getChartTooltipStyle } from "../utils/chartStyles";

type Mode = "percent" | "count";

type EndorsementGroup = {
  id: string;
  label: string;
  /** Endorsement types that belong to this group. Defaults to [id]. */
  types: string[];
};

type EndorsementsStackedChartProps = {
  data: EndorsementTypeCount[];
  totalActivePolicies: number;
  /** "percent" (default) shows normalized 0-100% bars; "count" shows raw counts */
  mode?: Mode;
};

/**
 * All possible endorsement groups with friendly labels.
 * Related types are combined into a single bar (e.g. terminal liability).
 */
const ALL_ENDORSEMENT_GROUPS: EndorsementGroup[] = [
  {
    id: "deductible_modification",
    label: "Deductible Modification",
    types: ["deductible_modification", "family_deductible", "tiered_deductible"],
  },
  { id: "aggregate_accommodation", label: "Aggregate Accommodation", types: ["aggregate_accommodation"] },
  { id: "experience_credit", label: "Experience Credit", types: ["experience_credit"] },
  { id: "experience_refund", label: "Experience Refund", types: ["experience_refund"] },
  { id: "rate_caps", label: "Rate Cap", types: ["rate_caps"] },
  { id: "no_new_lasers", label: "No New Laser", types: ["no_new_lasers"] },
  { id: "advance_funding", label: "Advanced Funding", types: ["advance_funding"] },
  { id: "admin_changes", label: "Admin Changes", types: ["admin_changes"] },
  { id: "leave_of_absence", label: "Leave of Absence", types: ["leave_of_absence"] },
  { id: "amendments", label: "Amendments", types: ["amendments"] },
  { id: "provider_dispute_credit", label: "Provider Dispute Credit", types: ["provider_dispute_credit"] },
  { id: "plan_mirroring", label: "Plan Mirroring", types: ["plan_mirroring"] },
  { id: "retained_corridor", label: "Retained Corridor", types: ["retained_corridor"] },
  {
    id: "terminal_liability",
    label: "Terminal Liability",
    types: ["specific_terminal_liability", "aggregate_terminal_liability", "contingent_terminal_liability"],
  },
  { id: "clinical_trials", label: "Clinical Trials", types: ["clinical_trials"] },
  { id: "transplant_coverage", label: "Transplant Coverage", types: ["transplant_coverage"] },
  { id: "travel_medical", label: "Travel Medical", types: ["travel_medical"] },
  { id: "reference_based_pricing", label: "Reference Based Pricing", types: ["reference_based_pricing"] },
  { id: "specialty_drug", label: "Specialty Drug", types: ["specialty_drug"] },
  { id: "exclusions", label: "Exclusions", types: ["exclusions"] },
  {
    id: "domestic_reimbursement_adjustments",
    label: "Domestic Reimbursement",
    types: ["domestic_reimbursement_adjustments"],
  },
];

export const EndorsementsStackedChart = ({
  data: endorsementCounts,
  totalActivePolicies,
  mode = "percent",
}: EndorsementsStackedChartProps) => {
  const { theme } = useAppTheme();

  if (!endorsementCounts?.length || totalActivePolicies === 0) return <EmptyChartState />;

  // Build a lookup from endorsement_type -> policy_count
  const countByType = new Map<string, number>();
  for (const item of endorsementCounts) {
    countByType.set(item.endorsement_type, (countByType.get(item.endorsement_type) ?? 0) + item.policy_count);
  }

  // Filter groups to only those with at least one present type
  const activeGroups = ALL_ENDORSEMENT_GROUPS.filter((group) => group.types.some((t) => (countByType.get(t) ?? 0) > 0));

  if (activeGroups.length === 0) return <EmptyChartState />;

  // Compute yes/no counts for each group
  const total = totalActivePolicies;
  const rows = activeGroups.map(({ label, types }) => {
    const yesCount = types.reduce((sum, t) => sum + (countByType.get(t) ?? 0), 0);
    const noCount = total - yesCount;

    return {
      endorsement: label,
      yes: yesCount,
      no: noCount,
      yesPct: total ? yesCount / total : 0,
      noPct: total ? noCount / total : 0,
      total,
    };
  });

  // Sort by adoption rate (highest first)
  rows.sort((a, b) => b.yesPct - a.yesPct);

  // Chart data depending on mode
  const data =
    mode === "percent"
      ? rows.map((r) => ({
          endorsement: r.endorsement,
          yes: r.yesPct,
          no: r.noPct,
          _counts: { yes: r.yes, no: r.no, total: r.total },
        }))
      : rows.map((r) => ({
          endorsement: r.endorsement,
          yes: r.yes,
          no: r.no,
          _counts: { yes: r.yes, no: r.no, total: r.total },
        }));

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: theme.palette.primary.main,
              borderRadius: 0.5,
            }}
          />
          <Typography variant="caption">Yes</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: theme.palette.background.default,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 0.5,
            }}
          />
          <Typography variant="caption">No</Typography>
        </Box>
      </Box>

      <ResponsiveContainer width="100%" height={280}>
        <BarChart
          data={data}
          layout="vertical"
          stackOffset={mode === "percent" ? "expand" : undefined}
          margin={{ top: 5, right: 15, left: 5, bottom: 5 }}
        >
          <YAxis
            type="category"
            dataKey="endorsement"
            width={140}
            tick={{ fontSize: 10 }}
            tickLine={false}
            axisLine={false}
          />
          <XAxis
            type="number"
            domain={mode === "percent" ? [0, 1] : ["auto", "auto"]}
            ticks={mode === "percent" ? [0, 0.25, 0.5, 0.75, 1] : undefined}
            tickFormatter={(val) => (mode === "percent" ? `${Math.round((val as number) * 100)}%` : `${val}`)}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 10 }}
          />
          <Tooltip
            content={({ payload }) => {
              if (!payload || !payload.length) return null;
              const item = payload[0].payload as (typeof data)[number];
              const counts = item._counts;
              const yesPct = counts.total ? ((counts.yes / counts.total) * 100).toFixed(1) : "0.0";
              const noPct = counts.total ? ((counts.no / counts.total) * 100).toFixed(1) : "0.0";
              return (
                <div
                  style={{
                    ...getChartTooltipStyle(theme),
                    padding: 8,
                    maxWidth: 260,
                  }}
                >
                  <strong>{item.endorsement}</strong>
                  <Divider sx={{ my: 1 }} />
                  <div>
                    Yes: <strong>{counts.yes}</strong>
                    {` (${yesPct}%)`}
                  </div>
                  <div>
                    No: <strong>{counts.no}</strong>
                    {` (${noPct}%)`}
                  </div>
                  <div style={{ opacity: 0.8, marginTop: 4 }}>Total policies: {counts.total}</div>
                </div>
              );
            }}
          />
          <Bar dataKey="yes" name="Yes" stackId="a" fill={theme.palette.primary.main} />
          <Bar
            dataKey="no"
            name="No"
            stackId="a"
            fill={theme.palette.background.default}
            stroke={theme.palette.divider}
            strokeWidth={1}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};
